<template>
  <div v-if="instance" class="space-y-4">
    <!-- Header -->
    <div class="bg-white border border-gray-200 rounded-lg overflow-hidden">
      <div class="px-6 py-4 bg-amber-50 border-b border-amber-100">
        <div class="flex justify-between items-start">
          <div>
            <h2 class="text-lg font-semibold text-gray-900">{{ instance.alert_name }}</h2>
            <p class="text-sm text-gray-500 mt-0.5">
              <router-link
                v-if="instance.entity_id"
                :to="`/entity/${instance.entity_id}`"
                class="sensitive text-primary-600 hover:text-primary-800 hover:underline"
              >{{ instance.entity_name }}</router-link>
              <span v-else class="sensitive">{{ instance.entity_name }}</span>
              <span class="text-gray-400">({{ instance.entity_type }})</span>
              &middot; {{ instance.observation_date }}
            </p>
          </div>
          <div class="flex items-center gap-2">
            <span :class="statusClass">{{ instance.status }}</span>
            <span :class="severityClass">{{ severity }}</span>
          </div>
        </div>
      </div>

      <div class="p-6">
        <!-- Metrics grid -->
        <dl class="grid grid-cols-3 gap-x-6 gap-y-4">
          <div>
            <dt class="text-xs font-medium text-gray-500">Rate</dt>
            <dd class="text-sm font-semibold text-gray-900 mt-0.5">{{ formatRate() }}</dd>
          </div>
          <div>
            <dt class="text-xs font-medium text-gray-500">Numerator / Denominator</dt>
            <dd class="text-sm text-gray-900 mt-0.5">{{ instance.numerator }} / {{ instance.denominator }}</dd>
          </div>
          <div>
            <dt class="text-xs font-medium text-gray-500">Attribution</dt>
            <dd class="text-sm text-gray-900 mt-0.5">{{ instance.attribution }} patients</dd>
          </div>
          <div v-if="instance.percentile_rank != null">
            <dt class="text-xs font-medium text-gray-500">Percentile Rank</dt>
            <dd class="text-sm text-gray-900 mt-0.5">{{ instance.percentile_rank.toFixed(1) }}%</dd>
          </div>
          <div>
            <dt class="text-xs font-medium text-gray-500">Assigned To</dt>
            <dd class="text-sm text-gray-900 mt-0.5">{{ instance.assigned_to_name || 'Unassigned' }}</dd>
          </div>
          <div>
            <dt class="text-xs font-medium text-gray-500">Last Status Change</dt>
            <dd class="text-sm text-gray-900 mt-0.5">{{ formatDate(instance.last_status_change) }}</dd>
          </div>
        </dl>

        <!-- Status note -->
        <div v-if="instance.status_note" class="mt-4 pt-4 border-t border-gray-100">
          <h3 class="text-xs font-medium text-gray-500 mb-1">Status Note</h3>
          <p class="sensitive text-sm text-gray-700 bg-gray-50 rounded p-2" data-demo-blur>{{ instance.status_note }}</p>
        </div>

        <!-- Actions -->
        <div class="mt-4 pt-4 border-t border-gray-100 flex gap-2">
          <button
            v-if="instance.status === 'active'"
            @click="updateStatus('acknowledged')"
            class="px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded hover:bg-blue-100"
          >Acknowledge</button>
          <button
            v-if="instance.status !== 'resolved'"
            @click="updateStatus('resolved')"
            class="px-3 py-1.5 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded hover:bg-green-100"
          >Resolve</button>
          <button
            @click="showAssign = true"
            class="px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-50 border border-gray-200 rounded hover:bg-gray-100"
          >Assign</button>
          <button
            @click="showNote = true"
            class="px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-50 border border-gray-200 rounded hover:bg-gray-100"
          >Add Note</button>
        </div>
      </div>
    </div>

    <!-- Entity-grain alerts have no patient rows: show the live per-entity
         breakdown behind the rolled-up numerator/denominator instead. -->
    <div v-if="instance.grain === 'entity'" class="pt-1">
      <div class="flex items-center gap-2 mb-1">
        <h3 class="text-sm font-semibold text-gray-900">Entity breakdown</h3>
        <span class="inline-flex items-center gap-1.5 px-2 py-0.5 text-xs font-medium rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
          <span class="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
          Live data
        </span>
      </div>
      <p class="text-xs text-gray-500 mb-3">
        Current standing of each entity behind this alert, from the latest weekly snapshot — it may differ from the summary above, which is frozen at the observation date the alert fired.
      </p>
      <PerformanceSummary
        :metric-ids="[instance.metric_id]"
        :year="instance.observation_year"
        :entity-id="instance.entity"
        lock-entity-type="Practice"
      />
    </div>

    <!-- Patient Worklist -->
    <PatientWorklist
      v-else
      :metric-ids="[instance.metric_id]"
      :year="instance.observation_year"
      :entity-id="instance.entity"
    />

    <!-- Assign modal -->
    <div v-if="showAssign" class="fixed inset-0 z-50 flex items-center justify-center">
      <div class="absolute inset-0 bg-black/25" @click="showAssign = false"></div>
      <div class="relative bg-white rounded-lg shadow-xl w-full max-w-sm p-6">
        <h3 class="text-sm font-medium text-gray-900 mb-3">Assign Alert</h3>
        <select v-model="selectedUserId" class="w-full text-sm border border-gray-300 rounded px-3 py-2">
          <option :value="null">Unassigned</option>
          <option v-for="user in users" :key="user.id" :value="user.id" class="sensitive">
            {{ user.last_name }}, {{ user.first_name }}
          </option>
        </select>
        <div class="flex justify-end gap-2 mt-4">
          <button @click="showAssign = false" class="px-3 py-1.5 text-xs text-gray-600 border border-gray-200 rounded hover:bg-gray-50">Cancel</button>
          <button @click="assignAlert" class="px-3 py-1.5 text-xs text-white bg-blue-600 rounded hover:bg-blue-700">Assign</button>
        </div>
      </div>
    </div>

    <!-- Note modal -->
    <div v-if="showNote" class="fixed inset-0 z-50 flex items-center justify-center">
      <div class="absolute inset-0 bg-black/25" @click="showNote = false"></div>
      <div class="relative bg-white rounded-lg shadow-xl w-full max-w-sm p-6">
        <h3 class="text-sm font-medium text-gray-900 mb-3">Status Note</h3>
        <textarea v-model="noteText" rows="4" class="w-full text-sm border border-gray-300 rounded px-3 py-2" placeholder="Enter note..."></textarea>
        <div class="flex justify-end gap-2 mt-4">
          <button @click="showNote = false" class="px-3 py-1.5 text-xs text-gray-600 border border-gray-200 rounded hover:bg-gray-50">Cancel</button>
          <button @click="saveNote" class="px-3 py-1.5 text-xs text-white bg-blue-600 rounded hover:bg-blue-700">Save</button>
        </div>
      </div>
    </div>
  </div>

  <div v-else-if="loading" class="flex items-center justify-center py-12">
    <LoadingIndicator />
  </div>
</template>

<script>
import axios from 'axios'
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue'
import PatientWorklist from '@/components/InsightsNew/PatientWorklist.vue'
import PerformanceSummary from '@/components/InsightsNew/PerformanceSummary.vue'

export default {
  name: 'ComposableAlertDetail',
  components: { LoadingIndicator, PatientWorklist, PerformanceSummary },

  props: {
    alertInstanceId: { type: [Number, String], required: true },
  },

  emits: ['close', 'updated'],

  data() {
    return {
      instance: null,
      loading: false,
      users: [],
      showAssign: false,
      showNote: false,
      selectedUserId: null,
      noteText: '',
    }
  },

  computed: {
    severity() {
      return (this.instance?.detection_config || {}).severity || 'medium'
    },
    severityClass() {
      const base = 'px-2 py-0.5 text-xs font-medium rounded-full'
      const s = this.severity
      if (s === 'critical') return `${base} bg-red-100 text-red-800`
      if (s === 'high') return `${base} bg-orange-100 text-orange-800`
      if (s === 'medium') return `${base} bg-amber-100 text-amber-800`
      return `${base} bg-blue-100 text-blue-800`
    },
    statusClass() {
      const base = 'px-2 py-0.5 text-xs font-medium rounded-full'
      const s = this.instance?.status
      if (s === 'active') return `${base} bg-red-100 text-red-800`
      if (s === 'acknowledged') return `${base} bg-blue-100 text-blue-800`
      return `${base} bg-green-100 text-green-800`
    },
  },

  watch: {
    alertInstanceId() { this.fetchInstance() },
  },

  async mounted() {
    await this.fetchInstance()
    this.fetchUsers()
  },

  methods: {
    async fetchInstance() {
      this.loading = true
      try {
        const response = await axios.get(`api/impact_analytics/composable-alert-instances/${this.alertInstanceId}/`)
        this.instance = response.data
      } catch (error) {
        console.error('Error fetching alert instance:', error)
      } finally {
        this.loading = false
      }
    },

    async fetchUsers() {
      try {
        const response = await axios.get('api/user/')
        this.users = response.data.sort((a, b) =>
          (a.last_name || '').localeCompare(b.last_name || '') || (a.first_name || '').localeCompare(b.first_name || '')
        )
      } catch (error) {
        console.error('Error fetching users:', error)
      }
    },

    async updateStatus(newStatus) {
      try {
        await axios.patch(`api/impact_analytics/composable-alert-instances/${this.alertInstanceId}/`, {
          status: newStatus,
        })
        this.instance.status = newStatus
        this.$emit('updated')
      } catch (error) {
        console.error('Error updating status:', error)
      }
    },

    async assignAlert() {
      try {
        await axios.patch(`api/impact_analytics/composable-alert-instances/${this.alertInstanceId}/`, {
          assigned_to: this.selectedUserId,
        })
        await this.fetchInstance()
        this.showAssign = false
        this.$emit('updated')
      } catch (error) {
        console.error('Error assigning alert:', error)
      }
    },

    async saveNote() {
      try {
        await axios.patch(`api/impact_analytics/composable-alert-instances/${this.alertInstanceId}/`, {
          status_note: this.noteText,
        })
        this.instance.status_note = this.noteText
        this.showNote = false
        this.$emit('updated')
      } catch (error) {
        console.error('Error saving note:', error)
      }
    },

    formatRate() {
      const dc = this.instance?.display_config || {}
      const format = dc.format || 'percentage'
      const rate = this.instance?.rate
      if (rate == null) return '—'
      if (format === 'count') return Math.round(this.instance.numerator).toLocaleString()
      if (format === 'decimal') return rate.toFixed(dc.decimal_places ?? 1)
      return (rate * 100).toFixed(dc.decimal_places ?? 1) + '%'
    },

    formatDate(date) {
      if (!date) return '—'
      return new Date(date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
    },
  },
}
</script>
